import {FC, useState, useEffect} from 'react'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import {isNotEmpty, QUERIES} from '../../../../_metronic/helpers'
import {
  initialPost,
  ClassifiedPost,
  ClassifiedCategory,
  ClassifiedSubCategory,
} from '../core/_models'
import clsx from 'clsx'
import {useListView} from '../core/ListViewProvider'
import {
  createClassifiedPost,
  updateClassifiedPost,
  getAllClassifiedCategories,
  getClassifiedSubCategoriesByCategory,
} from '../core/_requests'
import {useQueryResponse} from '../core/QueryResponseProvider'
import {useMutation, useQueryClient, useQuery} from 'react-query'
import {toast} from 'react-toastify'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'

const stripHtml = (value?: string | null) =>
  value
    ? value
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
    : ''

const quillModules = {
  toolbar: [
    [{header: [1, 2, 3, false]}],
    ['bold', 'italic', 'underline', 'strike'],
    [{list: 'ordered'}, {list: 'bullet'}],
    ['link'],
    ['clean'],
  ],
}

const quillFormats = ['header', 'bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'link']

type Props = {
  isPostLoading: boolean
  post: ClassifiedPost
}

const editPostSchema = Yup.object().shape({
  // Only keep title, category, subCategory, city, and state as required fields.
  title: Yup.string()
    .min(3, 'Minimum 3 characters')
    .max(200, 'Maximum 200 characters')
    .required('Title is required'),
  description: Yup.string().test('description-length', 'Minimum 10 characters', (value) => {
    const plain = stripHtml(value)
    if (!plain) {
      return true
    }
    return plain.length >= 10
  }),
  category: Yup.string().required('Category is required'),
  subCategory: Yup.string().required('SubCategory is required'),
  price: Yup.number().min(0, 'Price must be positive').nullable().notRequired(),
  mainImage: Yup.string(),
  location: Yup.object().shape({
    address: Yup.string(),
    city: Yup.string().required('City is required'),
    state: Yup.string().required('State is required'),
    zipCode: Yup.string().nullable().notRequired(),
  }),
  contactInfo: Yup.object().shape({
    name: Yup.string(),
    email: Yup.string().email('Invalid email').nullable().notRequired(),
    phone: Yup.string(),
  }),
})

const PostEditModalForm: FC<Props> = ({post, isPostLoading}) => {
  const {setItemIdForUpdate} = useListView()
  const {refetch} = useQueryResponse()

  const [postForEdit] = useState<ClassifiedPost>({
    ...post,
    title: post.title || initialPost.title,
    description: post.description || initialPost.description,
    price: post.price || initialPost.price,
    currency: post.currency || initialPost.currency,
    condition: post.condition || initialPost.condition,
    status: post.status || initialPost.status,
    mainImage: post.mainImage || '',
    images: post.images || [],
    location: post.location || initialPost.location,
    contactInfo: post.contactInfo || initialPost.contactInfo,
    // Extract IDs from category and subCategory if they are objects
    category:
      typeof post.category === 'object' && post.category?.id ? post.category.id : post.category,
    subCategory:
      typeof post.subCategory === 'object' && post.subCategory?.id
        ? post.subCategory.id
        : post.subCategory,
  })

  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) {
      refetch()
    }
    setItemIdForUpdate(undefined)
  }

  const queryClient = useQueryClient()

  const formik = useFormik({
    initialValues: postForEdit,
    validationSchema: editPostSchema,
    onSubmit: async (values, {setSubmitting}) => {
      setSubmitting(true)
      try {
        const cleanedDescription = stripHtml(values.description) ? values.description : ''
        const payload: ClassifiedPost = {...values, description: cleanedDescription}
        if (isNotEmpty(values.id)) {
          await updatePost.mutateAsync(payload)
        } else {
          await createPost.mutateAsync(payload)
        }
      } catch (ex) {
        console.error(ex)
      } finally {
        setSubmitting(false)
        cancel(true)
      }
    },
  })

  const createPost = useMutation((post: ClassifiedPost) => createClassifiedPost(post), {
    onSuccess: () => {
      queryClient.invalidateQueries([`${QUERIES.CLASSIFIED_POSTS_LIST}`])
      toast.success('Post created successfully')
    },
    onError: () => {
      toast.error('Failed to create post')
    },
  })

  const updatePost = useMutation((post: ClassifiedPost) => updateClassifiedPost(post), {
    onSuccess: () => {
      queryClient.invalidateQueries([`${QUERIES.CLASSIFIED_POSTS_LIST}`])
      toast.success('Post updated successfully')
    },
    onError: () => {
      toast.error('Failed to update post')
    },
  })

  // Fetch categories
  const {data: categories = []} = useQuery(
    QUERIES.CLASSIFIED_CATEGORIES_LIST,
    () => getAllClassifiedCategories(),
    {
      cacheTime: 5 * 60 * 1000, // 5 minutes
      staleTime: 1 * 60 * 1000, // 1 minute
    }
  )

  // Fetch subcategories when category changes
  const [subCategories, setSubCategories] = useState<ClassifiedSubCategory[]>([])

  // Load subcategories on mount if editing existing post
  useEffect(() => {
    const categoryId =
      typeof post.category === 'object' && post.category?.id ? post.category.id : post.category

    if (categoryId) {
      getClassifiedSubCategoriesByCategory(categoryId as string).then((data) => {
        setSubCategories(data)
      })
    }
  }, [])

  // Load subcategories when category changes
  useEffect(() => {
    if (formik.values.category) {
      getClassifiedSubCategoriesByCategory(formik.values.category as string).then((data) => {
        setSubCategories(data)
      })
    }
  }, [formik.values.category])

  const handleImageUpload = (
    e: React.ChangeEvent<HTMLInputElement>,
    field: 'mainImage' | 'images'
  ) => {
    const file = e.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onloadend = () => {
        if (field === 'mainImage') {
          formik.setFieldValue('mainImage', reader.result as string)
        } else {
          const currentImages = formik.values.images || []
          if (currentImages.length >= 3) {
            toast.error('Maximum 3 additional images allowed')
            return
          }
          formik.setFieldValue('images', [...currentImages, reader.result as string])
        }
      }
      reader.readAsDataURL(file)
    }
  }

  const removeImage = (index: number) => {
    const newImages = [...(formik.values.images || [])]
    newImages.splice(index, 1)
    formik.setFieldValue('images', newImages)
  }

  return (
    <>
      <form id='kt_modal_add_post_form' className='form' onSubmit={formik.handleSubmit} noValidate>
        {/* begin::Scroll */}
        <div
          className='d-flex flex-column scroll-y me-n7 pe-7'
          id='kt_modal_add_post_scroll'
          data-kt-scroll='true'
          data-kt-scroll-activate='{default: false, lg: true}'
          data-kt-scroll-max-height='auto'
          data-kt-scroll-dependencies='#kt_modal_add_post_header'
          data-kt-scroll-wrappers='#kt_modal_add_post_scroll'
          data-kt-scroll-offset='300px'
        >
          {/* begin::Input group - Title */}
          <div className='fv-row mb-7'>
            <label className='required fw-bold fs-6 mb-2'>Title</label>
            <input
              placeholder='Post title'
              {...formik.getFieldProps('title')}
              type='text'
              name='title'
              className={clsx(
                'form-control form-control-solid mb-3 mb-lg-0',
                {'is-invalid': formik.touched.title && formik.errors.title},
                {
                  'is-valid': formik.touched.title && !formik.errors.title,
                }
              )}
              autoComplete='off'
              disabled={formik.isSubmitting || isPostLoading}
            />
            {formik.touched.title && formik.errors.title && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.title}</span>
                </div>
              </div>
            )}
          </div>
          {/* end::Input group */}

          {/* begin::Input group - Description */}
          <div className='fv-row mb-7'>
            <label className='fw-bold fs-6 mb-2'>Description</label>
            <ReactQuill
              theme='snow'
              value={formik.values.description || ''}
              onChange={(value) => {
                const sanitizedValue = stripHtml(value) ? value : ''
                formik.setFieldValue('description', sanitizedValue)
              }}
              onBlur={() => formik.setFieldTouched('description', true)}
              modules={quillModules}
              formats={quillFormats}
              readOnly={formik.isSubmitting || isPostLoading}
              placeholder='Post description'
            />
            {formik.touched.description && formik.errors.description && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.description}</span>
                </div>
              </div>
            )}
          </div>
          {/* end::Input group */}

          {/* begin::Input group - Category & SubCategory */}
          <div className='row mb-7'>
            <div className='col-md-6'>
              <label className='required fw-bold fs-6 mb-2'>Category</label>
              <select
                {...formik.getFieldProps('category')}
                name='category'
                className={clsx(
                  'form-select form-select-solid',
                  {'is-invalid': formik.touched.category && formik.errors.category},
                  {
                    'is-valid': formik.touched.category && !formik.errors.category,
                  }
                )}
                disabled={formik.isSubmitting || isPostLoading}
              >
                <option value=''>Select Category</option>
                {categories.map((cat: ClassifiedCategory) => (
                  <option key={cat.id} value={cat.id || ''}>
                    {cat.icon} {cat.name}
                  </option>
                ))}
              </select>
              {formik.touched.category && formik.errors.category && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.category}</span>
                  </div>
                </div>
              )}
            </div>

            <div className='col-md-6'>
              <label className='required fw-bold fs-6 mb-2'>SubCategory</label>
              <select
                {...formik.getFieldProps('subCategory')}
                name='subCategory'
                className={clsx(
                  'form-select form-select-solid',
                  {'is-invalid': formik.touched.subCategory && formik.errors.subCategory},
                  {
                    'is-valid': formik.touched.subCategory && !formik.errors.subCategory,
                  }
                )}
                disabled={formik.isSubmitting || isPostLoading || !formik.values.category}
              >
                <option value=''>Select SubCategory</option>
                {subCategories.map((subCat) => (
                  <option key={subCat.id} value={subCat.id || ''}>
                    {subCat.name}
                  </option>
                ))}
              </select>
              {formik.touched.subCategory && formik.errors.subCategory && (
                <div className='fv-plugins-message-container'>
                  <div className='fv-help-block'>
                    <span role='alert'>{formik.errors.subCategory}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* end::Input group */}

          {/* begin::Input group - Price & Condition */}
          <div className='row mb-7'>
            <div className='col-md-6'>
              <label className='fw-bold fs-6 mb-2'>Price</label>
              <div className='input-group'>
                <span className='input-group-text'>$</span>
                <input
                  type='number'
                  {...formik.getFieldProps('price')}
                  name='price'
                  className='form-control form-control-solid'
                  placeholder='0.00'
                  disabled={formik.isSubmitting || isPostLoading}
                />
              </div>
            </div>

            <div className='col-md-6'>
              <label className='fw-bold fs-6 mb-2'>Condition</label>
              <select
                {...formik.getFieldProps('condition')}
                name='condition'
                className='form-select form-select-solid'
                disabled={formik.isSubmitting || isPostLoading}
              >
                <option value='New'>New</option>
                <option value='Like New'>Like New</option>
                <option value='Good'>Good</option>
                <option value='Fair'>Fair</option>
                <option value='Poor'>Poor</option>
                <option value='Not Available'>Not Available</option>
              </select>
            </div>
          </div>
          {/* end::Input group */}

          {/* begin::Input group - Main Image */}
          <div className='fv-row mb-7'>
            <label className='fw-bold fs-6 mb-2'>Main Image</label>
            <input
              type='file'
              accept='image/*'
              className='form-control form-control-solid'
              onChange={(e) => handleImageUpload(e, 'mainImage')}
              disabled={formik.isSubmitting || isPostLoading}
            />
            {formik.values.mainImage && (
              <div className='mt-3'>
                <img
                  src={formik.values.mainImage}
                  alt='Main'
                  style={{maxWidth: '200px', maxHeight: '200px'}}
                  className='img-thumbnail'
                />
              </div>
            )}
            {formik.touched.mainImage && formik.errors.mainImage && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.mainImage}</span>
                </div>
              </div>
            )}
          </div>
          {/* end::Input group */}

          {/* begin::Input group - Additional Images */}
          <div className='fv-row mb-7'>
            <label className='fw-bold fs-6 mb-2'>Additional Images (Max 3)</label>
            <input
              type='file'
              accept='image/*'
              className='form-control form-control-solid'
              onChange={(e) => handleImageUpload(e, 'images')}
              disabled={formik.isSubmitting || isPostLoading}
            />
            {formik.values.images && formik.values.images.length > 0 && (
              <div className='d-flex flex-wrap gap-3 mt-3'>
                {formik.values.images.map((img, index) => (
                  <div key={index} className='position-relative'>
                    <img
                      src={img}
                      alt={`Image ${index + 1}`}
                      style={{width: '100px', height: '100px', objectFit: 'cover'}}
                      className='img-thumbnail'
                    />
                    <button
                      type='button'
                      className='btn btn-icon btn-sm btn-danger position-absolute top-0 end-0'
                      onClick={() => removeImage(index)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* end::Input group */}

          {/* begin::Input group - Location */}
          <div className='fv-row mb-7'>
            <label className='required fw-bold fs-6 mb-2'>Location</label>
            <div className='row mb-3'>
              <div className='col-md-12'>
                <input
                  placeholder='Address'
                  {...formik.getFieldProps('location.address')}
                  type='text'
                  name='location.address'
                  className='form-control form-control-solid'
                  autoComplete='off'
                  disabled={formik.isSubmitting || isPostLoading}
                />
              </div>
            </div>
            <div className='row'>
              <div className='col-md-4 mb-3'>
                <input
                  placeholder='City*'
                  {...formik.getFieldProps('location.city')}
                  type='text'
                  name='location.city'
                  className='form-control form-control-solid'
                  autoComplete='off'
                  disabled={formik.isSubmitting || isPostLoading}
                />
              </div>
              <div className='col-md-4 mb-3'>
                <input
                  placeholder='State*'
                  {...formik.getFieldProps('location.state')}
                  type='text'
                  name='location.state'
                  className='form-control form-control-solid'
                  autoComplete='off'
                  disabled={formik.isSubmitting || isPostLoading}
                />
              </div>
              <div className='col-md-4 mb-3'>
                <input
                  placeholder='Zip Code'
                  {...formik.getFieldProps('location.zipCode')}
                  type='text'
                  name='location.zipCode'
                  className='form-control form-control-solid'
                  autoComplete='off'
                  disabled={formik.isSubmitting || isPostLoading}
                />
              </div>
            </div>
          </div>
          {/* end::Input group */}

          {/* begin::Input group - Contact Info */}
          <div className='fv-row mb-7'>
            <label className='fw-bold fs-6 mb-2'>Contact Information</label>
            <div className='row mb-3'>
              <div className='col-md-12'>
                <input
                  placeholder='Contact Name'
                  {...formik.getFieldProps('contactInfo.name')}
                  type='text'
                  name='contactInfo.name'
                  className='form-control form-control-solid'
                  autoComplete='off'
                  disabled={formik.isSubmitting || isPostLoading}
                />
              </div>
            </div>
            <div className='row mb-3'>
              <div className='col-md-6'>
                <input
                  placeholder='Email'
                  {...formik.getFieldProps('contactInfo.email')}
                  type='email'
                  name='contactInfo.email'
                  className='form-control form-control-solid'
                  autoComplete='off'
                  disabled={formik.isSubmitting || isPostLoading}
                />
              </div>
              <div className='col-md-6'>
                <input
                  placeholder='Phone'
                  {...formik.getFieldProps('contactInfo.phone')}
                  type='text'
                  name='contactInfo.phone'
                  className='form-control form-control-solid'
                  autoComplete='off'
                  disabled={formik.isSubmitting || isPostLoading}
                />
              </div>
            </div>
          </div>
          {/* end::Input group */}

          {/* begin::Input group - Status */}
          <div className='fv-row mb-7'>
            <label className='fw-bold fs-6 mb-2'>Status</label>
            <select
              {...formik.getFieldProps('status')}
              name='status'
              className='form-select form-select-solid'
              disabled={formik.isSubmitting || isPostLoading}
            >
              <option value='Active'>Active</option>
              <option value='Inactive'>Inactive</option>
              <option value='Sold'>Sold</option>
              <option value='Expired'>Expired</option>
            </select>
          </div>
          {/* end::Input group */}
        </div>
        {/* end::Scroll */}

        {/* begin::Actions */}
        <div className='text-center pt-15'>
          <button
            type='reset'
            onClick={() => cancel()}
            className='btn btn-light me-3'
            data-kt-users-modal-action='cancel'
            disabled={formik.isSubmitting || isPostLoading}
          >
            Discard
          </button>

          <button
            type='submit'
            className='btn btn-primary'
            data-kt-users-modal-action='submit'
            disabled={isPostLoading || formik.isSubmitting || !formik.isValid || !formik.touched}
          >
            <span className='indicator-label'>Submit</span>
            {(formik.isSubmitting || isPostLoading) && (
              <span className='indicator-progress'>
                Please wait...{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
        {/* end::Actions */}
      </form>
      {(formik.isSubmitting || isPostLoading) && <div className='loading-overlay' />}
    </>
  )
}

export {PostEditModalForm}
